import React, { forwardRef } from "react";
import { FormControl, MenuItem, Checkbox, ListItemText } from "@mui/material";
import { styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import "../../theme-vars.css";

type Option = { value: string | number; label: string };

type Props = {
  title?: string;
  option?: string[]; // same as old one
  choices?: Option[];
  value: any;
  onChange: (val: any) => void;
  setDetails?: () => void;
  isMulti?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  required?: boolean;
};

const primaryColor = "#292744";

/**
 * Custom styling for form, input label, select, and menu items.
 * Uses CSS variables from `theme-vars.css`.
 */
const CustomFormControl = styled(FormControl)({
  minWidth: "100%",

  /** Label styles */
  "& .MuiInputLabel-root": {
    color: primaryColor,
    fontWeight: 500,
    fontSize: 'var(--font-size-medium)',
    fontFamily: 'var(--font-family-main)',
    background: "#f7f8fa",
    padding: "0 4px",
  },
  "& .MuiInputLabel-root.Mui-required .MuiInputLabel-asterisk": {
    color: "#d32f2f", // red color for required asterisk
  },

  /** Outline styles */
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },

  /** Select text */
  "& .MuiSelect-select": {
    padding: "14px",
    fontSize: 'var(--font-size-medium)',
    fontFamily: 'var(--font-family-main)',
    color: "#222",
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
  },
  "& .MuiMenu-paper": {
    fontFamily: "var(--font-family-main)", // <-- add here
    fontSize: "var(--font-size-medium)",   // <-- add here
  },
  "& .MuiMenuItem-root": {
    fontFamily: "var(--font-family-main)", // <-- add here
    fontSize: "var(--font-size-medium)",   // <-- add here
  },
  "& .MuiListItemText-root": {
    fontFamily: "var(--font-family-main)", // <-- add here
    fontSize: "var(--font-size-medium)",   // <-- add here
  },
});

/** ------------------------------
 *  MAIN COMPONENT
 * ------------------------------ */
const Dropdown = forwardRef<HTMLInputElement, Props>(
  ({ title, option, choices, value, onChange, setDetails, isMulti, style, disabled, required }, ref) => {

    /** Build option set */
    const buildOptions = (): Option[] => {
      let data: Option[] = [];
      if (option) {
        data = option.map((item: any) =>
          item === "Team Lead"
            ? { value: item, label: "Team Leader" }
            : { value: item, label: item }
        );
        if (title) {
          data.push({ value: title, label: title });
        }
      } else {
        data = choices || [];
      }
      return data;
    };

    const opts = buildOptions();

    /** Handle value change */
    const handleChange = (event: SelectChangeEvent<any>) => {
      const selectedVal = isMulti ? event.target.value : event.target.value;
      if (!isMulti && selectedVal === title && setDetails) {
        setDetails();
      }
      onChange(selectedVal);
    };

    return (
      <CustomFormControl
        variant="outlined"
        style={style}
        disabled={disabled || opts.length === 0}
        required={required}
      >
        {title && (
          <InputLabel
            style={{
              color: "#555",
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-medium)",
            }}
            required={required}
          >
            {title}
          </InputLabel>
        )}
        <Select
          multiple={isMulti}
          value={value || (isMulti ? [] : "")}
          onChange={handleChange}
          required={required}
          renderValue={(selected) =>
            isMulti
              ? (selected as string[]).join(", ")
              : opts.find((o) => o.value === selected)?.label || ""
          }
        >
          {opts.map((opt, index) => (
            <MenuItem
              key={index}
              value={opt.value}
            >
              {isMulti && (
                <Checkbox checked={(value || []).indexOf(opt.value) > -1} />
              )}
              <ListItemText
                primaryTypographyProps={{
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-medium)",
                }}
                primary={opt.label}
              />
            </MenuItem>
          ))}
        </Select>
      </CustomFormControl>
    );
  }
);

export default Dropdown;
